import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, NgModule, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, FormsModule, Validators } from '@angular/forms';
import { Cms } from '@impact/data';
import { TranslateModule } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FormElementsModule } from '../../../lib/form-elements/form-elements.module';
import { BREAKPOINTS, BreakpointService } from '../../breakpoint/breakpoint.service';
import { ButtonModule } from '../../button/button.module';
import { ConsentModule } from '../../consent/consent.module';
import { ConsentService } from '../../core/consent.service';
import { INewsletterSubscribe, NewsletterService } from '../../core/newsletter.service';
import { SettingsService } from '../../core/settings.service';
import { FormModule } from '../../form/form.module';
import { FormService } from '../../form/form.service';
import { SectionHeaderModule } from '../../section-header/section-header.module';

@Component({
    selector: 'app-newsletter-signup-spot',
    template: `
        <div class="newsletter-signup-spot">
            <app-section-header
                [headerCategoryText]="data.newsletterSignupCategoryHeaderText"
                [headerTextPrimary]="data.newsletterSignupHeaderText"
                [isCentered]="true"
            >
            </app-section-header>

            <ng-container [ngSwitch]="hasSubmitted">
                <p class="newsletter-signup-spot__content-submitted" *ngSwitchCase="true">
                    {{ data.newsletterSignupSuccessText }}
                </p>
                <div class="newsletter-signup-spot__content" *ngSwitchCase="false">
                    <p class="newsletter-signup-spot__content-text" *ngIf="data.newsletterSignupText">{{ data.newsletterSignupText }}</p>
                    <form
                        *ngIf="form"
                        [formGroup]="form"
                        (ngSubmit)="onSubmit()"
                        class="newsletter-signup-spot__form"
                        [ngClass]="{ 'is-submitting': isSubmitting }"
                    >
                        <div class="newsletter-signup-spot__form-inputs">
                            <app-input
                                autocomplete="email"
                                cssClasses="input__input-element--white-background"
                                controlName="email"
                                [parentForm]="form"
                                class="user-info-input"
                                [label]="'user_information.email' | translate"
                                type="email"
                            >
                            </app-input>
                            <app-input
                                autocomplete="first-name"
                                cssClasses="input__input-element--white-background"
                                controlName="firstName"
                                [parentForm]="form"
                                class="user-info-input"
                                [label]="'user_information.first_name' | translate"
                                type="text"
                            >
                            </app-input>
                            <app-input
                                autocomplete="last-name"
                                cssClasses="input__input-element--white-background"
                                controlName="lastName"
                                [parentForm]="form"
                                class="user-info-input"
                                [label]="'user_information.last_name' | translate"
                                type="text"
                            >
                            </app-input>

                            <app-button *ngIf="isLargeDevice$ | async" buttonType="submit" buttonClass="button--primary button--has-spacing button--inside-inline-form" [loading]="isSubmitting">
                                {{ data.newsletterSignupSubmitButtonText }}
                            </app-button>
                        </div>
                        <app-personal-information-consent class="newsletter-signup-spot__consent" [parentForm]="form" controlName="consent">
                        </app-personal-information-consent>
                        <app-button *ngIf="(isLargeDevice$ | async) === false" buttonType="submit" buttonClass="button--primary button--has-spacing" [loading]="isSubmitting">
                            {{ data.newsletterSignupSubmitButtonText }}
                        </app-button>
                    </form>
                    <p *ngIf="data.newsletterSignupDisclaimerText" class="newsletter-signup-spot__disclaimer-text">
                        {{ data.newsletterSignupDisclaimerText }}
                    </p>
                </div>
            </ng-container>
        </div>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
class NewsletterSignupSpotComponent implements OnInit, OnDestroy {
    static ref = 'newslettersignupspot';

    /**
     * Since this spot is either from a regular grid *or* from the footer settings, we
     * use both the Umbraco type and the settings DTO.
     */
    @Input() data: Cms.NewsletterSignUpSpot | Cms.NewslettersSignUpSpotDto;

    form: UntypedFormGroup;
    consent: any;
    isSubmitting = false;
    hasSubmitted = false;
    siteId: string;

    private readonly unsubscribe = new Subject<void>(); 

     emailControl: any;
     firstNameControl: any;
     lastNameControl: any;
     consentControl: any;
     isLargeDevice$: any;

    constructor(
        public readonly breakpointService: BreakpointService,
        readonly fb: UntypedFormBuilder,
        private readonly cdr: ChangeDetectorRef,
        private readonly formService: FormService,
        private readonly newsletterService: NewsletterService,
        private readonly consentService: ConsentService,
        private readonly settingsService: SettingsService
    ) {
        this.consentService
            .getCheckoutConsentText()
            .pipe(takeUntil(this.unsubscribe))
            .subscribe((res) => {
                this.consent = res;
            });

        this.settingsService.getSiteId().pipe(takeUntil(this.unsubscribe)).subscribe((siteId) => {
            this.siteId = siteId;
        });

        this.form = fb.group({
            email: ['', [Validators.required, Validators.email]],
            firstName: ['', Validators.required],
            lastName: ['', Validators.required],
            consent: ['', Validators.required],
        });
    }

    ngOnInit(): void {

        this.emailControl = this.form.get('email');
        this.firstNameControl = this.form.get('firstName');
        this.lastNameControl = this.form.get('lastName');
        this.consentControl = this.form.get('consent');
        this.isLargeDevice$ = this.breakpointService.matchBreakpoint(BREAKPOINTS.LARGE);
        this.formService.trackInitialization(NewsletterSignupSpotComponent.ref);
    }

    onSubmit() {
        if (!this.form.valid) {
            this.form.markAllAsTouched();
            this.isSubmitting = false;
            return;
        }

        if (this.consentControl?.value === false) {
            this.consentControl.setErrors({ incorrect: true });
            FormService.markControlsAsTouched(this.form);
            return;
        }

        this.isSubmitting = true;

        const payload: INewsletterSubscribe = {
            firstName: this.firstNameControl?.value,
            lastName: this.lastNameControl?.value,
            email: this.emailControl?.value,
            consentRevisionAcceptedGuid: this.consent?.revisionGuid ?? '',
            siteName: this.siteId
        };

        this.newsletterService
            .newsletterSignup(payload)
            .pipe(takeUntil(this.unsubscribe))
            .subscribe(() => {
                this.hasSubmitted = true;
                this.isSubmitting = false;
                this.cdr.markForCheck();
                this.formService.trackCompletion(NewsletterSignupSpotComponent.ref, !!payload.consentRevisionAcceptedGuid);
            });
    }

    ngOnDestroy(): void {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }
}

@NgModule({
    declarations: [NewsletterSignupSpotComponent],
    imports: [CommonModule, ButtonModule, SectionHeaderModule, TranslateModule, FormModule, FormsModule, FormElementsModule, ConsentModule],
})
class NewsletterSignupSpotModule {}

export const LazySpotComponent = NewsletterSignupSpotComponent;
